import { createColumnHelper, SortingFnOption } from '@tanstack/react-table';
import moment from 'moment';
import ReactCountryFlag from 'react-country-flag';
import { BsFillImageFill } from 'react-icons/bs';
import { FaFire } from 'react-icons/fa';
import { PiCoins } from 'react-icons/pi';
import AssociateFeedbackHolder from '../components/AssociateFeedbackComps/AssociateFeedbackHolder';
import AssociateRequestDetails from '../components/AssociateRequestsComps/AssociateRequestDetails';
import AssociateDetails from '../components/AssociatesComps/AssociateDetails';
import ShowLogDetailsBtn from '../components/AuditLogComps/ShowLogDetailsBtn';
import ClientFeedbackHolder from '../components/ClientFeedbackComps/ClientFeedbackHolder';
import ClientDetails from '../components/ClientsComps/ClientDetails';
import AcademiaRating from '../components/CommonComps/AcademiaRating';
import SkillBadge from '../components/CommonComps/Badges/SkillBadge';
import CommonUpdateAction from '../components/CommonComps/CommonUpdateAction';
import ProfilePictureRowDiv from '../components/CommonComps/ProfilePictureRowDiv';
import AppointmentDetailsCol from '../components/HistoryComps/AppointmentComps/AppointmentDetailsCol';
import RescheduleAppointmentAction from '../components/HistoryComps/AppointmentComps/RescheduleAppointmentAction';
import ReferralClientNameDiv from '../components/HistoryComps/ReferralsComps/ReferralClientNameDiv';
import RescheduleHistoryUpdateActions from '../components/HistoryComps/RescheduleComps/RescheduleHistoryUpdateActions';
import ViewRescheduleDetails from '../components/HistoryComps/RescheduleComps/ViewRescheduleDetails';
import ShowRetainDetailsBtn from '../components/HistoryComps/RetainComps/ShowRetainDetailsBtn';
import ServiceUpdateAction from '../components/ServicesComps/ServiceUpdateAction';
import ConfigurationDetails from '../components/SetupSubPageComps/ConfigurationsComps/ConfigurationDetails';
import RolePermissionTableRow from '../components/SetupSubPageComps/RolePermissionsComps/RolePermissionTableRow';
import VacationRequestUpdateActions from '../components/VacationsComps/VacationRequestUpdateActions';
import { Associate } from '../types/associates';
import { AuditLog } from '../types/auditLogs';
import { AppointmentSummary } from '../types/common';
import { Config } from '../types/configurations';
import {
  DashboardBookingHistory,
  DashboardTransactionHistory,
} from '../types/dashboard';
import { AssociateFeedback, ClientFeedback } from '../types/feedbacks';
import {
  AppointmentHistory,
  BillingHistory,
  ManualTransactionHistory,
  PurchaseHistory,
  RescheduleHistory,
  TransactionHistory,
} from '../types/history';
import { AssociateReferral, Referral } from '../types/referral';
import { Reminder } from '../types/reminders';
import { RetainAgreement } from '../types/retain';
import { RolePermissionTableData } from '../types/rolePermissions';
import { Service } from '../types/services';
import { Client } from '../types/users';
import { VacationRequest } from '../types/vacations';
import { formatFeatureName, showFullName } from '../utils/commonFunctions';
import { showTimeIn12HrsFormat } from '../utils/timeAndDate';
import { TABLE_BODY_SPAN_CLASSES, TABLE_HEAD_SPAN_CLASSES } from './settings';

// creating column helper here
const tranHistoryColumnHelper =
  createColumnHelper<DashboardTransactionHistory>();
const bookHistoryColumnHelper = createColumnHelper<DashboardBookingHistory>();
const summaryColumnHelper = createColumnHelper<AppointmentSummary>();
const associateColumnHelper = createColumnHelper<Associate>();
const associateRequestColumnHelper = createColumnHelper<Associate>();
const clientColumnHelper = createColumnHelper<Client>();
const configColumnHelper = createColumnHelper<Config>();
const rolePermissionColumnHelper =
  createColumnHelper<RolePermissionTableData>();
const vacationRequestColumnHelper = createColumnHelper<VacationRequest>();
const serviceColumnHelper = createColumnHelper<Service>();
const appointmentHistoryColumnHelper = createColumnHelper<AppointmentHistory>();
const rescheduleHistoryColumnHelper = createColumnHelper<RescheduleHistory>();
const referralHistoryColumnHelper = createColumnHelper<Referral>();
const retainHistoryColumnHelper = createColumnHelper<RetainAgreement>();
const referralHistoryReferredByColumnHelper =
  createColumnHelper<AssociateReferral>();
const transactionHistoryColumnHelper = createColumnHelper<TransactionHistory>();
const manualTransactionHistoryColumnHelper =
  createColumnHelper<ManualTransactionHistory>();
const purchaseHistoryColumnHelper = createColumnHelper<PurchaseHistory>();
const billingHistoryColumnHelper = createColumnHelper<BillingHistory>();
const clientFeedbackColumnHelper = createColumnHelper<ClientFeedback>();
const associateFeedbackColumnHelper = createColumnHelper<AssociateFeedback>();
const sentReminderColumnHelper = createColumnHelper<Reminder>();
const auditLogsColumnHelper = createColumnHelper<AuditLog>();

// transaction history for dashboard columns declared here
export const transactionHistoryColumns = [
  tranHistoryColumnHelper.accessor((row) => row.id, {
    id: 'tranId',
    cell: (info) => info.getValue(),
    header: () => <span className='inline-block min-w-[70px]'>Sl. No.</span>,
  }),

  tranHistoryColumnHelper.accessor((row) => row.transaction_id, {
    id: 'dashTranTId',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Transaction ID</span>
    ),
  }),

  tranHistoryColumnHelper.accessor((row) => row.transaction_date, {
    id: 'dashTranDate',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Transaction Date</span>
    ),
  }),

  tranHistoryColumnHelper.accessor((row) => row.transaction_time, {
    id: 'dashTranTime',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Transaction Date</span>
    ),
  }),

  tranHistoryColumnHelper.accessor((row) => row.paid_amount, {
    id: 'dashTranPaid',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Paid Amount</span>
    ),
  }),

  tranHistoryColumnHelper.accessor((row) => row.purchased_credit, {
    id: 'dashTranPurchasedCredit',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Purchased Credits</span>
    ),
  }),
];

export const bookHistoryColumns = [
  bookHistoryColumnHelper.accessor((row) => row.id, {
    id: 'bookId',
    cell: (info) => info.getValue(),
    header: () => <span className='inline-block min-w-[70px]'>Sl. No.</span>,
  }),

  bookHistoryColumnHelper.accessor((row) => row.associate_name, {
    id: 'dashBookAssociate',
    cell: (info) => info.getValue(),
    header: () => <span className='inline-block min-w-[150px]'>Associate</span>,
  }),

  bookHistoryColumnHelper.accessor((row) => row.appointment_date, {
    id: 'dashBookDate',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Appointment Date</span>
    ),
  }),

  bookHistoryColumnHelper.accessor((row) => row.starting_time, {
    id: 'dashBookStart',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Starting Time</span>
    ),
  }),

  bookHistoryColumnHelper.accessor((row) => row.ending_time, {
    id: 'dashBookEnd',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Ending Time</span>
    ),
  }),

  bookHistoryColumnHelper.accessor((row) => row.duration, {
    id: 'dashBookDuration',
    cell: (info) => info.getValue(),
    header: () => <span className='inline-block min-w-[150px]'>Duration</span>,
  }),

  bookHistoryColumnHelper.accessor((row) => row.credit_used, {
    id: 'dashBookCredit',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Credit Used</span>
    ),
  }),
];

// appointment summary columns declared here
export const summaryColumns = [
  summaryColumnHelper.accessor((row) => row.id, {
    id: 'sumId',
    cell: (info) => info.getValue(),
    header: () => <span className='inline-block min-w-[70px]'>Sl. No.</span>,
  }),

  summaryColumnHelper.accessor((row) => row.client_name, {
    id: 'clientName',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Client Name</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.client_email, {
    id: 'clientEmail',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Client Email</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.client_location, {
    id: 'clientLocation',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Client Location</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.appointment_date, {
    id: 'AppointmentDate',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Appointment Date</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.allocated_time, {
    id: 'allocatedTime',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Allocated Time</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.total_duration, {
    id: 'totalDuration',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[150px]'>Total Duration</span>
    ),
  }),

  summaryColumnHelper.accessor((row) => row.status, {
    id: 'status',
    cell: (info) => (
      <span
        className={`font-medium italic ${
          info.getValue() === 'Completed'
            ? 'text-base-dark-green'
            : info.getValue() === 'Pending'
            ? 'text-base-orange'
            : 'text-base-purple'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className='inline-block min-w-[150px]'>Status</span>,
  }),
];

// configurations table columns declared here
export const configTableColumns = [
  configColumnHelper.accessor((row) => row.id, {
    id: 'configId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  configColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'configAssociateName',
    cell: (info) => <ConfigurationDetails config={info.row.original} />,
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[200px] capitalize'>
        Associate
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  configColumnHelper.accessor((row) => row.service_name, {
    id: 'configServiceName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[100px] capitalize'>
        Service
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  configColumnHelper.accessor((row) => row.lead_time, {
    id: 'lead_time',
    cell: (info) =>
      `${showTimeIn12HrsFormat(info.getValue())} - ${showTimeIn12HrsFormat(
        info.row.original.finish_time
      )}`,
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[150px] capitalize'>
        Working Hours
      </span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<Config>,
  }),

  configColumnHelper.accessor((row) => row.slot_duration, {
    id: 'configSlotDuration',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[100px] capitalize'>
        Duration
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  configColumnHelper.accessor((row) => row.appointment_buffer_time, {
    id: 'configBufferTime',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[150px] capitalize'>
        Buffer Time
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  configColumnHelper.accessor((row) => row.reschedule_limit, {
    id: 'configRescheduleLimit',
    cell: (info) => info.getValue(),
    header: () => (
      <span className='inline-block min-w-[250px] 2xl:min-w-[160px] capitalize'>
        Reschedule Limit
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  configColumnHelper.accessor((row) => row.working_days_with_names, {
    id: 'configWorkingDays',
    cell: (info) => (
      <div className='flex flex-wrap gap-1'>
        {info.getValue().map((day) => (
          <SkillBadge
            key={day.value}
            badgeLabel={day.day.slice(0, 3)}
            color='#388229'
          />
        ))}
      </div>
    ),
    header: () => (
      <span className='inline-block min-w-[400px] 2xl:min-w-[300px]'>
        Working Days
      </span>
    ),
    enableSorting: false,
  }),

  configColumnHelper.accessor((row) => row.id, {
    id: 'configActions',
    cell: (info) => (
      <CommonUpdateAction
        data={info.row.original}
        deleteBtnLabel='delete Configuration'
        updateBtnLabel='update Configuration'
        featureName='serviceConfig'
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// vacation request table columns declared here
export const vacationRequestTableColumns = [
  vacationRequestColumnHelper.accessor((row) => row.id, {
    id: 'vacReqId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  vacationRequestColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'vacReqAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Associate</span>,
    sortingFn: 'alphanumeric',
  }),

  vacationRequestColumnHelper.accessor((row) => row.start_date, {
    id: 'start_date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<VacationRequest>,
  }),

  vacationRequestColumnHelper.accessor((row) => row.end_date, {
    id: 'end_date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Date</span>,
    sortingFn: 'sortByDate' as SortingFnOption<VacationRequest>,
  }),

  vacationRequestColumnHelper.accessor((row) => row.description, {
    id: 'vacReqDescription',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Description</span>,
    sortingFn: 'alphanumeric',
  }),

  vacationRequestColumnHelper.accessor((row) => row.rejected_note, {
    id: 'vacReqComments',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Comments</span>,
    sortingFn: 'alphanumeric',
  }),

  vacationRequestColumnHelper.accessor((row) => row.status, {
    id: 'vacReqStatus',
    cell: (info) => (
      <span
        className={`font-medium italic ${
          info.getValue() === 'Approved'
            ? 'text-base-cyan'
            : info.getValue() === 'Rejected'
            ? 'text-base-red'
            : 'text-base-orange'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  vacationRequestColumnHelper.accessor((row) => row.id, {
    id: 'vacationActions',
    cell: (info) => (
      <VacationRequestUpdateActions request={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// services table columns declared here
export const servicesTableColumns = [
  serviceColumnHelper.accessor((row) => row.id, {
    id: 'serviceId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  serviceColumnHelper.accessor((row) => row.image, {
    id: 'serviceImage',
    cell: (info) => (
      <div className='mx-auto size-10'>
        {info.getValue() ? (
          <img
            className='h-full w-full object-cover rounded-md'
            src={`${
              process.env.REACT_APP_APPOINTMENT_SERVICE_PROD_API
            }${info.getValue()}`}
            height={0}
            width={0}
            sizes='100%'
            alt={info.row.original.name}
            loading='lazy'
          />
        ) : (
          <div className='size-10'>
            <BsFillImageFill className='size-10 text-gray-300' />
          </div>
        )}
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Image</span>,
    enableSorting: false,
  }),

  serviceColumnHelper.accessor((row) => row.name, {
    id: 'serviceName',
    cell: (info) => (
      <div className='flex items-center justify-center gap-3'>
        <span>{info.getValue()}</span>
        {info.row.original.is_most_popular ? (
          <span title='Popular Service'>
            <FaFire className='text-base-orange' />
          </span>
        ) : null}
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Name</span>,
    sortingFn: 'alphanumeric',
  }),

  serviceColumnHelper.accessor((row) => row.description, {
    id: 'serviceDescription',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Description</span>,
    sortingFn: 'alphanumeric',
  }),

  serviceColumnHelper.accessor((row) => row.background_color, {
    id: 'serviceBackgroundColor',
    cell: (info) => (
      <div
        style={{ backgroundColor: info.getValue() }}
        className='h-6 w-12 mx-auto rounded-md'
      ></div>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Background Color</span>
    ),
    enableSorting: false,
  }),

  serviceColumnHelper.accessor((row) => row.id, {
    id: 'serviceActions',
    cell: (info) => <ServiceUpdateAction service={info.row.original} />,
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// appointment history table columns for client declared here
export const appointmentHistoryTableColumnsClient = [
  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appClientId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'appClientAssociateName',
    cell: (info) => <AppointmentDetailsCol history={info.row.original} />,
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.date, {
    id: 'date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Appointment Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.start_time, {
    id: 'start_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Time</span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.end_time, {
    id: 'end_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Time</span>,
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.status, {
    id: 'appAssociateStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium italic ${
            info.getValue() === 'Rescheduled'
              ? 'text-base-light-blue'
              : info.getValue() === 'Unattended'
              ? 'text-base-orange'
              : info.getValue() === 'Upcoming'
              ? 'text-base-purple'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appClientActions',
    cell: (info) => (
      <RescheduleAppointmentAction appointmentHistory={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// appointment history table columns for associates declared here
export const appointmentHistoryTableColumnsAssociate = [
  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appAssociateId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'appAssociateClientName',
    cell: (info) => (
      <AppointmentDetailsCol history={info.row.original} isAssociate />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.date, {
    id: 'date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Appointment Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.start_time, {
    id: 'start_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Time</span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.end_time, {
    id: 'end_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Time</span>,
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.status, {
    id: 'appAssociateStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium italic ${
            info.getValue() === 'Rescheduled'
              ? 'text-base-light-blue'
              : info.getValue() === 'Unattended'
              ? 'text-base-orange'
              : info.getValue() === 'Upcoming'
              ? 'text-base-purple'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appAssociateActions',
    cell: (info) => (
      <RescheduleAppointmentAction appointmentHistory={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// appointment history table columns for admins declared here
export const appointmentHistoryTableColumnsAdmin = [
  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appAssociateId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'appClientAssociateName',
    cell: (info) => <AppointmentDetailsCol history={info.row.original} />,
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'appAssociateClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.date, {
    id: 'date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Appointment Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.start_time, {
    id: 'start_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Time</span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.end_time, {
    id: 'end_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Time</span>,
    sortingFn: 'sortByTime' as SortingFnOption<AppointmentHistory>,
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.status, {
    id: 'appAssociateStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium italic ${
            info.getValue() === 'Rescheduled'
              ? 'text-base-light-blue'
              : info.getValue() === 'Unattended'
              ? 'text-base-orange'
              : info.getValue() === 'Upcoming'
              ? 'text-base-purple'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  appointmentHistoryColumnHelper.accessor((row) => row.id, {
    id: 'appAssociateActions',
    cell: (info) => (
      <RescheduleAppointmentAction appointmentHistory={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// reschedule history table columns declared here
export const rescheduleHistoryTableColumns = [
  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'historyClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor(
    (row) => row.prev_associate_full_name,
    {
      id: 'historyPrevAssociateName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned By</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  rescheduleHistoryColumnHelper.accessor((row) => row.new_associate_full_name, {
    id: 'historyNewAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned To</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.previous_date, {
    id: 'previous_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Previous Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_date, {
    id: 'reschedule_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Rescheduled Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_status, {
    id: 'historyStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium capitalize italic ${
            info.getValue() === 'rejected'
              ? 'text-base-red'
              : info.getValue() === 'pending'
              ? 'text-base-orange'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyAction',
    cell: (info) => (
      <ViewRescheduleDetails rescheduleHistory={info.row.original} />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'rescheduleHistoryActions',
    cell: (info) => (
      <RescheduleHistoryUpdateActions rescheduleHistory={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// reschedule history table columns for associate assigned to declared here
export const rescheduleHistoryTableAssociateColumnsTo = [
  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'historyClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor(
    (row) => row.prev_associate_full_name,
    {
      id: 'historyPrevAssociateName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned By</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  rescheduleHistoryColumnHelper.accessor((row) => row.previous_date, {
    id: 'previous_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Previous Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_date, {
    id: 'reschedule_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Rescheduled Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_status, {
    id: 'historyStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium capitalize italic ${
            info.getValue() === 'rejected'
              ? 'text-base-red'
              : info.getValue() === 'pending'
              ? 'text-base-orange'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyAction',
    cell: (info) => (
      <ViewRescheduleDetails rescheduleHistory={info.row.original} />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),
];

// reschedule history table columns for associate assigned by declared here
export const rescheduleHistoryTableAssociateColumnsBy = [
  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'historyClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.new_associate_full_name, {
    id: 'historyNewAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned To</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.previous_date, {
    id: 'previous_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Previous Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_date, {
    id: 'reschedule_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Rescheduled Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_status, {
    id: 'historyStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium capitalize italic ${
            info.getValue() === 'rejected'
              ? 'text-base-red'
              : info.getValue() === 'pending'
              ? 'text-base-orange'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyAction',
    cell: (info) => (
      <ViewRescheduleDetails rescheduleHistory={info.row.original} />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'rescheduleHistoryActions',
    cell: (info) => (
      <RescheduleHistoryUpdateActions rescheduleHistory={info.row.original} />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// reschedule history table columns for client declared here
export const rescheduleHistoryTableClientColumns = [
  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  rescheduleHistoryColumnHelper.accessor(
    (row) => row.prev_associate_full_name,
    {
      id: 'historyPrevAssociateName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned By</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  rescheduleHistoryColumnHelper.accessor((row) => row.new_associate_full_name, {
    id: 'historyNewAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Assigned To</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.previous_date, {
    id: 'previous_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Previous Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_date, {
    id: 'reschedule_date',
    cell: (info) => (
      <span title={info.getValue()} className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Rescheduled Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<RescheduleHistory>,
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.reschedule_status, {
    id: 'historyStatus',
    cell: (info) => (
      <div className='w-full truncate'>
        <span
          className={`font-medium capitalize italic ${
            info.getValue() === 'rejected'
              ? 'text-base-red'
              : info.getValue() === 'pending'
              ? 'text-base-orange'
              : 'text-base-cyan'
          }`}
        >
          {info.getValue()}
        </span>
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  rescheduleHistoryColumnHelper.accessor((row) => row.id, {
    id: 'historyAction',
    cell: (info) => (
      <ViewRescheduleDetails rescheduleHistory={info.row.original} />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),
];

// referral history table columns declared here
export const referralHistoryTableColumns = [
  referralHistoryColumnHelper.accessor((row) => row.id, {
    id: 'refId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  referralHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'refClientName',
    cell: (info) => <ReferralClientNameDiv referral={info.row.original} />,
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  referralHistoryColumnHelper.accessor((row) => row.referrer_full_name, {
    id: 'referrerName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Referred By</span>,
    sortingFn: 'alphanumeric',
  }),

  referralHistoryColumnHelper.accessor((row) => row.referred_at, {
    id: 'referred_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Referred At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<Referral>,
  }),

  referralHistoryColumnHelper.accessor((row) => row.responded_at, {
    id: 'responded_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Responded At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<Referral>,
  }),

  referralHistoryColumnHelper.accessor((row) => row.status, {
    id: 'refStatus',
    cell: (info) => (
      <span
        className={`${
          info.getValue() === 'Accepted' ? 'text-base-cyan' : 'text-base-orange'
        } font-medium italic`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),
];

// referral history table columns [REFERRED BY] declared here
export const referralHistoryTableReferredByColumns = [
  referralHistoryReferredByColumnHelper.accessor((row) => row.id, {
    id: 'refId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  referralHistoryReferredByColumnHelper.accessor(
    (row) => row.client_full_name,
    {
      id: 'refClientName',
      cell: (info) => <ReferralClientNameDiv referral={info.row.original} />,
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  referralHistoryReferredByColumnHelper.accessor(
    (row) => row.referred_by_full_name,
    {
      id: 'referrerName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Referred By</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  referralHistoryReferredByColumnHelper.accessor((row) => row.referred_at, {
    id: 'referred_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Referred At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AssociateReferral>,
  }),

  referralHistoryReferredByColumnHelper.accessor((row) => row.responded_at, {
    id: 'responded_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Responded At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AssociateReferral>,
  }),

  referralHistoryReferredByColumnHelper.accessor((row) => row.status, {
    id: 'refStatus',
    cell: (info) => (
      <span
        className={`${
          info.getValue() === 'Accepted' ? 'text-base-cyan' : 'text-base-orange'
        } font-medium italic`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),
];

// referral history table columns [REFERRED TO] declared here
export const referralHistoryTableReferredToColumns = [
  referralHistoryReferredByColumnHelper.accessor((row) => row.id, {
    id: 'refId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  referralHistoryReferredByColumnHelper.accessor(
    (row) => row.client_full_name,
    {
      id: 'refClientName',
      cell: (info) => <ReferralClientNameDiv referral={info.row.original} />,
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  referralHistoryReferredByColumnHelper.accessor(
    (row) => row.referred_to_full_names,
    {
      id: 'referredName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>
          {info.getValue().join(', ')}
        </span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Referred To</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  referralHistoryReferredByColumnHelper.accessor((row) => row.referred_at, {
    id: 'referred_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Referred At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AssociateReferral>,
  }),

  referralHistoryReferredByColumnHelper.accessor((row) => row.responded_at, {
    id: 'responded_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Responded At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AssociateReferral>,
  }),

  referralHistoryReferredByColumnHelper.accessor((row) => row.status, {
    id: 'refStatus',
    cell: (info) => (
      <span
        className={`${
          info.getValue() === 'Accepted' ? 'text-base-cyan' : 'text-base-orange'
        } font-medium italic`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),
];

export const retainHistoryColumns = [
  retainHistoryColumnHelper.accessor((row) => row.id, {
    id: 'retainId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  retainHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'retainAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Associate</span>,
    sortingFn: 'alphanumeric',
  }),

  retainHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'retainClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client</span>,
    sortingFn: 'alphanumeric',
  }),

  retainHistoryColumnHelper.accessor((row) => row.start_date, {
    id: 'start_date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Start Date</span>,
    sortingFn: 'sortByDate' as SortingFnOption<RetainAgreement>,
  }),

  retainHistoryColumnHelper.accessor((row) => row.end_date, {
    id: 'end_date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>End Date</span>,
    sortingFn: 'sortByDate' as SortingFnOption<RetainAgreement>,
  }),

  retainHistoryColumnHelper.accessor(
    (row) => (row.is_active ? 'Active' : 'Inactive'),
    {
      id: 'retainStatus',
      cell: (info) => (
        <span
          className={`${
            info.getValue() === 'Active' ? 'text-base-cyan' : 'text-base-red'
          } font-medium italic`}
        >
          {info.getValue()}
        </span>
      ),
      header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
      sortingFn: 'sortByDate' as SortingFnOption<RetainAgreement>,
    }
  ),

  retainHistoryColumnHelper.accessor((row) => row.id, {
    id: 'retainAction',
    cell: (info) => <ShowRetainDetailsBtn retainHistory={info.row.original} />,
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),
];

// transaction history table columns declared here
export const transactionHistoryTableColumns = [
  transactionHistoryColumnHelper.accessor((row) => row.id, {
    id: 'transId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  transactionHistoryColumnHelper.accessor((row) => row.transaction_id, {
    id: 'transactionId',
    cell: (info) => (
      <div className='w-full truncate text-base-light-blue font-medium'>
        {info.getValue()}
      </div>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Transaction ID</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  transactionHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'transClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  transactionHistoryColumnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Date</span>,
    sortingFn: 'sortByDate' as SortingFnOption<TransactionHistory>,
  }),

  transactionHistoryColumnHelper.accessor((row) => row.cost_amount, {
    id: 'transPaidAmount',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Paid Amount</span>,
    sortingFn: 'alphanumeric',
  }),

  transactionHistoryColumnHelper.accessor((row) => row.credits_purchased, {
    id: 'transPurchasedCredit',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Purchased Credit</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  transactionHistoryColumnHelper.accessor((row) => row.payment_status, {
    id: 'transPaymentStatus',
    cell: (info) => (
      <span
        className={`font-medium capitalize italic ${
          info.getValue() === 'completed'
            ? 'text-base-cyan'
            : 'text-base-orange'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),
];

// transaction history table columns declared here
export const manualTransactionHistoryTableColumns = [
  manualTransactionHistoryColumnHelper.accessor((row) => row.id, {
    id: 'transId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  manualTransactionHistoryColumnHelper.accessor((row) => row.transaction_id, {
    id: 'transactionId',
    cell: (info) => (
      <div className='w-full truncate text-base-light-blue font-medium'>
        {info.getValue()}
      </div>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Transaction ID</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  manualTransactionHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'transClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  manualTransactionHistoryColumnHelper.accessor((row) => row.transaction_date, {
    id: 'transaction_date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Date</span>,
    sortingFn: 'sortByDate' as SortingFnOption<ManualTransactionHistory>,
  }),

  manualTransactionHistoryColumnHelper.accessor((row) => row.payment_method, {
    id: 'transPayMethod',
    cell: (info) => <span className='capitalize'>{info.getValue()}</span>,
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Payment Method</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  manualTransactionHistoryColumnHelper.accessor((row) => row.cost_amount, {
    id: 'transPaidAmount',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Paid Amount</span>,
    sortingFn: 'alphanumeric',
  }),

  manualTransactionHistoryColumnHelper.accessor(
    (row) => row.credits_purchased,
    {
      id: 'transPurchasedCredit',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Purchased Credit</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  manualTransactionHistoryColumnHelper.accessor((row) => row.id, {
    id: 'billingActions',
    cell: (info) => (
      <CommonUpdateAction
        data={info.row.original}
        deleteBtnLabel='delete transaction'
        updateBtnLabel='update transaction'
        featureName='' // MJA: FEATURE NAME REQUIRED
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// purchase history table columns declared here
export const purchaseHistoryTableColumns = [
  purchaseHistoryColumnHelper.accessor((row) => row.id, {
    id: 'purId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.client_full_name, {
    id: 'purClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'purAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.date, {
    id: 'date',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Transaction Date</span>
    ),
    sortingFn: 'sortByDate' as SortingFnOption<PurchaseHistory>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.start_time, {
    id: 'start_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Time</span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<PurchaseHistory>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.end_time, {
    id: 'end_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Time</span>,
    sortingFn: 'sortByTime' as SortingFnOption<PurchaseHistory>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.total_minutes, {
    id: 'purDuration',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()} Mins.</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Duration</span>,
    sortingFn: 'alphanumeric',
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.credits_cost, {
    id: 'purCreditUsed',
    cell: (info) => (
      <span
        className={`flex items-center justify-center gap-2 ${TABLE_BODY_SPAN_CLASSES}`}
      >
        <PiCoins className='size-5' /> {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Credit used</span>,
    sortingFn: 'alphanumeric',
  }),
];

// purchase history table columns for client declared here
export const purchaseHistoryTableColumnsClient = [
  purchaseHistoryColumnHelper.accessor((row) => row.id, {
    id: 'purId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'purAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  purchaseHistoryColumnHelper.accessor(
    (row) => moment(row.created_at).format('DD-MM-YYYY'),
    {
      id: 'created_at',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Transaction Date</span>
      ),
      sortingFn: 'sortByDate' as SortingFnOption<PurchaseHistory>,
    }
  ),

  purchaseHistoryColumnHelper.accessor((row) => row.start_time, {
    id: 'start_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Starting Time</span>
    ),
    sortingFn: 'sortByTime' as SortingFnOption<PurchaseHistory>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.end_time, {
    id: 'end_time',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ending Time</span>,
    sortingFn: 'sortByTime' as SortingFnOption<PurchaseHistory>,
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.total_minutes, {
    id: 'purDuration',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()} Mins.</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Duration</span>,
    sortingFn: 'alphanumeric',
  }),

  purchaseHistoryColumnHelper.accessor((row) => row.credits_cost, {
    id: 'purCreditUsed',
    cell: (info) => (
      <span
        className={`flex items-center justify-center gap-2 ${TABLE_BODY_SPAN_CLASSES}`}
      >
        <PiCoins className='size-5' /> {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Credit used</span>,
    sortingFn: 'alphanumeric',
  }),
];

// billing history table columns declared here
export const billingHistoryTableColumns = [
  billingHistoryColumnHelper.accessor((row) => row.id, {
    id: 'billId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  billingHistoryColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'billAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.bank_account_number, {
    id: 'billBankAcc',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Bank Account No.</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.payment_method, {
    id: 'billPayMethod',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Payment Method</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.routing_number, {
    id: 'billRoutingNo',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Routing No.</span>,
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.tax_id, {
    id: 'billTaxId',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Tax Id</span>,
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Created At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<BillingHistory>,
  }),

  configColumnHelper.accessor((row) => row.id, {
    id: 'billingActions',
    cell: (info) => (
      <CommonUpdateAction
        data={info.row.original}
        deleteBtnLabel='delete bill history'
        updateBtnLabel='update bill history'
        featureName='' // MJA: FEATURE NAME REQUIRED
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

export const billingHistoryTableAssociateColumns = [
  billingHistoryColumnHelper.accessor((row) => row.id, {
    id: 'billId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  billingHistoryColumnHelper.accessor((row) => row.bank_account_number, {
    id: 'billBankAcc',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Bank Account No.</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.payment_method, {
    id: 'billPayMethod',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Payment Method</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.routing_number, {
    id: 'billRoutingNo',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Routing No.</span>,
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.tax_id, {
    id: 'billTaxId',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>
        {info.getValue() || 'N/A'}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Tax Id</span>,
    sortingFn: 'alphanumeric',
  }),

  billingHistoryColumnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Created At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<BillingHistory>,
  }),
];

// client feedback table columns declared here
export const clientFeedbackTableColumns = [
  clientFeedbackColumnHelper.accessor((row) => row.id, {
    id: 'clientFeedId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  clientFeedbackColumnHelper.accessor((row) => row.client_full_name, {
    id: 'clientFeedClientName',
    cell: (info) => <ClientFeedbackHolder feedback={info.row.original} />,
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  clientFeedbackColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'clientFeedAssociateName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  clientFeedbackColumnHelper.accessor((row) => row.rating, {
    id: 'clientFeedRating',
    cell: (info) => (
      <AcademiaRating
        size={35}
        ratingValue={info.getValue()}
        isReadonly
        extraClassNames='w-full truncate justify-center'
      />
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Ratings</span>,
    enableSorting: false,
  }),

  clientFeedbackColumnHelper.accessor((row) => row.comments, {
    id: 'clientFeedComments',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Comments</span>,
    sortingFn: 'alphanumeric',
  }),

  clientFeedbackColumnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    cell: (info) => info.getValue(),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Created At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<ClientFeedback>,
  }),
];

// associate feedback table columns declared here
export const associateFeedbackTableColumns = [
  associateFeedbackColumnHelper.accessor((row) => row.id, {
    id: 'associateFeedId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  associateFeedbackColumnHelper.accessor((row) => row.associate_full_name, {
    id: 'associateFeedAssociateName',
    cell: (info) => <AssociateFeedbackHolder feedback={info.row.original} />,
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  associateFeedbackColumnHelper.accessor((row) => row.client_full_name, {
    id: 'associateFeedClientName',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  associateFeedbackColumnHelper.accessor((row) => row.comments, {
    id: 'associateFeedComments',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Comments</span>,
    sortingFn: 'alphanumeric',
  }),

  associateFeedbackColumnHelper.accessor((row) => row.created_at, {
    id: 'created_at',
    cell: (info) => info.getValue(),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Created At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AssociateFeedback>,
  }),
];

// sent reminder table columns declared here
export const sentReminderTableColumns = [
  sentReminderColumnHelper.accessor((row) => row.id, {
    id: 'reminderId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>{info.getValue()}</span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  sentReminderColumnHelper.accessor((row) => row.client, {
    id: 'reminderClient',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>,
    sortingFn: 'alphanumeric',
  }),

  sentReminderColumnHelper.accessor((row) => row.associate, {
    id: 'reminderAssociate',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => (
      <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
    ),
    sortingFn: 'alphanumeric',
  }),

  sentReminderColumnHelper.accessor((row) => row.message, {
    id: 'reminderMessage',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Sent Message</span>,
    sortingFn: 'alphanumeric',
  }),

  sentReminderColumnHelper.accessor((row) => row.is_read, {
    id: 'reminderIsRead',
    cell: (info) => (
      <div className='w-full truncate font-medium italic'>
        {info.getValue() ? (
          <p className='text-base-cyan'>Seen</p>
        ) : (
          <p className='text-base-orange'>Unseen</p>
        )}
      </div>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),

  serviceColumnHelper.accessor((row) => row.id, {
    id: 'serviceActions',
    cell: (info) => (
      <CommonUpdateAction
        deleteBtnLabel='delete reminder'
        updateBtnLabel='update reminder'
        featureName='' // MJA: FEATURE NAME REQUIRED
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// associates table columns declared here
export const associatesTableColumns = [
  associateColumnHelper.accessor((row) => row.id, {
    id: 'associateId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  associateColumnHelper.accessor((row) => row.profile_picture, {
    id: 'associateProfilePicture',
    cell: (info) => (
      <ProfilePictureRowDiv
        profilePicture={info.getValue()}
        username={info.row.original.username}
      />
    ),
    header: () => <span className='inline-block min-w-[40px]'>Image</span>,
    enableSorting: false,
  }),

  associateColumnHelper.accessor(
    (row) => `${showFullName(row.first_name, row.middle_name, row.last_name)}`,
    {
      id: 'associateName',
      cell: (info) => <AssociateDetails associate={info.row.original} />,
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  associateColumnHelper.accessor((row) => row.role_name, {
    id: 'associateRole',
    cell: (info) => (
      <span
        className={`font-medium ${
          info.getValue() === 'Admin'
            ? 'text-base-purple'
            : info.getValue() === 'Associate'
            ? 'text-base-light-blue'
            : 'text-base-orange'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Role</span>,
    sortingFn: 'alphanumeric',
  }),

  associateColumnHelper.accessor((row) => row.email, {
    id: 'associateEmail',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Email</span>,
    sortingFn: 'alphanumeric',
  }),

  associateColumnHelper.accessor((row) => `${row.isd_code}${row.phone}`, {
    id: 'associatePhoneNo',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Phone Number</span>,
    enableSorting: false,
  }),

  associateColumnHelper.accessor(
    (row) => `${row.address}, ${row.city_name}, ${row.province_name}`,
    {
      id: 'associateAddress',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Address</span>,
      sortingFn: 'alphanumeric',
    }
  ),

  associateColumnHelper.accessor((row) => row.country_name, {
    id: 'associateCountry',
    cell: (info) => (
      <span className={`${TABLE_BODY_SPAN_CLASSES} space-x-1`}>
        <span>
          <ReactCountryFlag
            style={{ width: '1.8em', height: '1.22em' }}
            countryCode={info.row.original.iso_code}
            svg
          />
        </span>{' '}
        <span>{info.getValue()}</span>
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Country</span>,
    sortingFn: 'alphanumeric',
  }),

  associateColumnHelper.accessor(
    (row) => (row.is_active ? 'Active' : 'Inactive'),
    {
      id: 'associateStatus',
      cell: (info) => (
        <span
          className={`font-medium italic ${
            info.getValue() === 'Active' ? 'text-base-cyan' : 'text-base-red'
          }`}
        >
          {info.getValue()}
        </span>
      ),
      header: () => <span className='inline-block min-w-[100px]'>Status</span>,
      sortingFn: 'alphanumeric',
    }
  ),

  associateColumnHelper.accessor((row) => row.id, {
    id: 'userActions',
    cell: (info) => (
      <CommonUpdateAction
        deleteBtnLabel='delete associate'
        updateBtnLabel='update associate'
        deactivateBtnLabel='associate'
        rejectBtnLabel='Reject associate'
        featureName='users'
        data={info.row.original}
        isUsersPage
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

// associate request table columns declared here
export const associateRequestTableColumns = [
  associateRequestColumnHelper.accessor((row) => row.id, {
    id: 'associateReqId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  associateRequestColumnHelper.accessor((row) => row.profile_picture, {
    id: 'associateReqProfilePicture',
    cell: (info) => (
      <ProfilePictureRowDiv
        profilePicture={info.getValue()}
        username={info.row.original.username}
      />
    ),
    header: () => <span className='inline-block min-w-[40px]'>Image</span>,
    enableSorting: false,
  }),

  associateRequestColumnHelper.accessor(
    (row) => `${showFullName(row.first_name, row.middle_name, row.last_name)}`,
    {
      id: 'associateReqName',
      cell: (info) => <AssociateRequestDetails associate={info.row.original} />,
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Associate Name</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  associateRequestColumnHelper.accessor((row) => row.email, {
    id: 'associateReqEmail',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Email</span>,
    sortingFn: 'alphanumeric',
  }),

  associateRequestColumnHelper.accessor(
    (row) => `${row.isd_code}${row.phone}`,
    {
      id: 'associateReqPhoneNo',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Phone Number</span>
      ),
      enableSorting: false,
    }
  ),

  associateRequestColumnHelper.accessor(
    (row) => `${row.address}, ${row.country_name}`,
    {
      id: 'associateReqAddress',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Address</span>,
      sortingFn: 'alphanumeric',
    }
  ),

  associateRequestColumnHelper.accessor((row) => row.country_name, {
    id: 'associateCountry',
    cell: (info) => (
      <span className={`${TABLE_BODY_SPAN_CLASSES} space-x-1`}>
        <span>
          <ReactCountryFlag
            style={{ width: '1.8em', height: '1.22em' }}
            countryCode={info.row.original.iso_code}
            svg
          />
        </span>{' '}
        <span>{info.getValue()}</span>
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Country</span>,
    sortingFn: 'alphanumeric',
  }),

  associateRequestColumnHelper.accessor((row) => row.status, {
    id: 'associateReqStatus',
    cell: (info) => (
      <span
        className={`${TABLE_BODY_SPAN_CLASSES} font-medium capitalize italic ${
          info.getValue() === 'resubmit'
            ? !info.row.original.is_resubmit
              ? 'text-base-purple'
              : 'text-base-cyan'
            : info.getValue() === 'rejected'
            ? 'text-base-red'
            : info.getValue() === 'pending'
            ? 'text-base-orange'
            : ''
        }`}
      >
        {info.getValue() === 'resubmit'
          ? !info.row.original.is_resubmit
            ? info.getValue()
            : 'Resubmitted'
          : info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Status</span>,
    sortingFn: 'alphanumeric',
  }),
];

// clients table columns declared here
export const clientsTableColumns = [
  clientColumnHelper.accessor((row) => row.id, {
    id: 'clientId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  clientColumnHelper.accessor((row) => row.profile_picture, {
    id: 'clientProfilePicture',
    cell: (info) => (
      <ProfilePictureRowDiv
        profilePicture={info.getValue()}
        username={info.row.original.username}
      />
    ),
    header: () => <span className='inline-block min-w-[40px]'>Image</span>,
    enableSorting: false,
  }),

  clientColumnHelper.accessor(
    (row) => showFullName(row.first_name, row.middle_name, row.last_name, true),
    {
      id: 'clientName',
      cell: (info) => <ClientDetails client={info.row.original} />,
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Client Name</span>
      ),
      sortingFn: 'alphanumeric',
    }
  ),

  clientColumnHelper.accessor((row) => row.email, {
    id: 'clientEmail',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Email</span>,
    sortingFn: 'alphanumeric',
  }),

  clientColumnHelper.accessor(
    (row) => (row.phone ? `${row.isd_code}${row.phone}` : 'N/A'),
    {
      id: 'clientPhoneNo',
      cell: (info) => info.getValue(),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Phone Number</span>
      ),
      enableSorting: false,
    }
  ),

  clientColumnHelper.accessor(
    (row) =>
      row.address
        ? `${row.address}, ${row.city_name}, ${row.province_name}`
        : 'N/A',
    {
      id: 'clientAddress',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
      ),
      header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Address</span>,
      sortingFn: 'alphanumeric',
    }
  ),

  clientColumnHelper.accessor((row) => row.country_name, {
    id: 'clientCountry',
    cell: (info) => (
      <span className={`${TABLE_BODY_SPAN_CLASSES} space-x-1`}>
        {info.getValue() ? (
          <>
            {' '}
            <span>
              <ReactCountryFlag
                style={{ width: '1.8em', height: '1.22em' }}
                countryCode={info.row.original.iso_code}
                svg
              />
            </span>{' '}
            <span>{info.getValue()}</span>
          </>
        ) : (
          'N/A'
        )}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Country</span>,
    sortingFn: 'alphanumeric',
  }),

  clientColumnHelper.accessor((row) => row.last_login_date, {
    id: 'clientLastLogin',
    cell: (info) =>
      info.getValue()
        ? moment(info.getValue()).format('DD-MM-YYYY @hh:mm A')
        : 'N/A',
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Last Login</span>,
    enableSorting: false,
  }),

  clientColumnHelper.accessor((row) => row.is_active, {
    id: 'clientActive',
    cell: (info) => (
      <span
        className={`font-medium italic ${
          info.getValue() ? 'text-base-cyan' : 'text-base-red'
        }`}
      >
        {info.getValue() ? 'Active' : 'Deactive'}
      </span>
    ),
    header: () => (
      <span className='inline-block min-w-[250px] lg:min-w-[100px] capitalize'>
        Status
      </span>
    ),
    sortingFn: 'alphanumeric',
  }),

  clientColumnHelper.accessor((row) => row.id, {
    id: 'clientActions',
    cell: (info) => (
      <CommonUpdateAction
        deleteBtnLabel='delete client'
        updateBtnLabel='update client'
        deactivateBtnLabel='client'
        featureName='client'
        data={info.row.original}
        isUsersPage
      />
    ),
    header: () => <span className='inline-block min-w-[20px]'></span>,
    enableSorting: false,
  }),
];

export const auditLogTableColumns = [
  auditLogsColumnHelper.accessor((row, index) => index, {
    id: 'auditId',
    cell: (info) => (
      <span className='font-medium text-base-dark-blue'>
        {info.row.index + 1}
      </span>
    ),
    header: () => <span className='inline-block min-w-[40px]'>#</span>,
  }),

  auditLogsColumnHelper.accessor((row) => row.perform_by_profile_picture, {
    id: 'associateReqProfilePicture',
    cell: (info) => (
      <ProfilePictureRowDiv
        profilePicture={info.getValue()}
        username={info.row.original.perform_by_full_name}
      />
    ),
    header: () => <span className='inline-block min-w-[40px]'>Image</span>,
    enableSorting: false,
  }),

  auditLogsColumnHelper.accessor((row) => row.perform_by_full_name, {
    id: 'auditPerformedBy',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Performed By</span>,
    sortingFn: 'alphanumeric',
  }),

  auditLogsColumnHelper.accessor((row) => row.perform_by_role, {
    id: 'auditPerformRole',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>User Role</span>,
    sortingFn: 'alphanumeric',
  }),

  // auditLogsColumnHelper.accessor((row) => row.model_name, {
  //   id: 'auditModelName',
  //   cell: (info) => (
  //     <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
  //   ),
  //   header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Model Name</span>,
  //   sortingFn: 'alphanumeric',
  // }),

  auditLogsColumnHelper.accessor((row) => row.action, {
    id: 'auditAction',
    cell: (info) => (
      <span
        className={`${TABLE_BODY_SPAN_CLASSES} uppercase font-medium italic ${
          info.getValue() === 'update' ? 'text-base-cyan' : 'text-base-red'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    sortingFn: 'alphanumeric',
  }),

  auditLogsColumnHelper.accessor((row) => row.action_time, {
    id: 'performed_at',
    cell: (info) => (
      <span className={TABLE_BODY_SPAN_CLASSES}>{info.getValue()}</span>
    ),
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Performed At</span>,
    sortingFn: 'sortByDate' as SortingFnOption<AuditLog>,
  }),

  auditLogsColumnHelper.accessor((row) => row.id, {
    id: 'auditLogDetails',
    cell: (info) => <ShowLogDetailsBtn auditLog={info.row.original} />,
    header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>Action</span>,
    enableSorting: false,
  }),
];

// function to generate role permission table columns declared here
export const generateRolePermissionTableColumn = (roleList: Array<string>) => {
  return [
    rolePermissionColumnHelper.accessor((row) => row.id, {
      id: 'rolePermissionId',
      cell: (info) => (
        <span className='font-medium text-base-dark-blue'>
          {info.row.index + 1}
        </span>
      ),
      header: () => <span className='inline-block min-w-[40px]'>#</span>,
    }),

    rolePermissionColumnHelper.accessor((row) => row.feature_name, {
      id: 'permissionFeatureName',
      cell: (info) => (
        <span className={TABLE_BODY_SPAN_CLASSES}>
          {formatFeatureName(info.getValue())}
        </span>
      ),
      header: () => (
        <span className={TABLE_HEAD_SPAN_CLASSES}>Feature Name</span>
      ),
      sortingFn: 'alphanumeric',
    }),

    ...roleList.map((role) =>
      rolePermissionColumnHelper.accessor(
        (row) => row.permissions[role]?.permission_names || [],
        {
          id: `permission${role}`,
          cell: (info) => (
            <RolePermissionTableRow permissions={info.getValue()} />
          ),
          header: () => <span className={TABLE_HEAD_SPAN_CLASSES}>{role}</span>,
          enableSorting: false,
        }
      )
    ),
  ];
};
